<template>
  <el-form-item :label="label" :prop="prop" class="input-phone-wrapper">
    <div class="input-phone__content">
      <el-select v-model="activeCountry"
                 value-key="iso2"
                 filterable
                 class="country-select select-default"
                 ref="select">
        <img class="country-option__icon selected-country__icon"
             @click="focusSelect"
             :src="activeCountry.icon"
             :alt="activeCountry.name"
             slot="prefix">
        <el-option v-for="country in allCountries"
                   class="country-option__item select-default"
                   :label="country.name"
                   :value="country"
                   :key="country.iso2">
          <div class="country-option">
            <img :src="country.icon" :alt="country.name" class="country-option__icon">
            <span class="country-option__text">{{country.name}}</span>
          </div>
        </el-option>
      </el-select>
      <el-input :value="phone"
                :state="isValid"
                v-on="listeners"
                v-bind="$attrs"
                class="a-input">
      </el-input>
    </div>
  </el-form-item>
</template>
<script>
  import { format, AsYouType, isValidNumber } from 'libphonenumber-js'
  import { Select, Option, Input } from 'element-ui'
  import allCountries from '../../util/all-countries'

  export default {
    name: 'phone-input',
    components: {
      [Input.name]: Input,
      [Select.name]: Select,
      [Option.name]: Option,
    },
    inheritAttrs: false,
    props: {
      value: {
        type: String,
        default: ''
      },
      label: String,
      prop: {
        type: String,
        default: ''
      },
      prefix: {
        type: String,
        default: ''
      },
      detectCountry: {
        type: Boolean,
        default: true
      }
    },
    data() {
      return {
        phone: '',
        countries: [],
        activeCountry: {},
        phoneMeta: {
          number: '',
          prefix: '+1'
        }
      }
    },
    computed: {
      allCountries() {
        let countries = this.$store.getters['countries']
        return allCountries.filter(c => {
          return countries.find(localCountry => localCountry.code.toLowerCase() === c.iso2.toLowerCase()) !== undefined
        })
      },
      isValid() {
        return isValidNumber(this.formattedResult)
      },
      mode() {
        if (!this.phone) {
          return ''
        }
        if (this.phone[0] === '+') {
          return 'code'
        }
        if (this.phone[0] === '0') {
          return 'prefix'
        }
        return 'normal'
      },
      formattedResult() {
        // Calculate phone number based on mode
        if (!this.mode || !this.allCountries) {
          return ''
        }
        let phone = this.phone
        if (this.mode === 'prefix') {
          // Remove the first '0' if this is a '0' prefix number
          // Ex: 0432421999
          phone = this.phone.slice(1)
        }
        return format(phone, this.activeCountry && this.activeCountry.iso2, 'International')
      },
      listeners() {
        return {
          ...this.$listeners,
          input: this.handleInput,
          blur: this.handleBlur
        }
      },
      inputData() {
        return {
          number: this.removeWhitespaces(this.phone),
          activeCountry: this.activeCountry,
          isValid: this.isValid
        }
      }
    },
    watch: {
      isValid(value) {
        if (value) {
          this.phone = this.formattedResult
          this.emitInput(this.phone)
        }
      },
      async phone(newValue) {
        if (this.mode === 'code') {
          // If user manually type the country code
          await this.formatAndSetCountry(newValue)
        }
      },
      activeCountry(newVal) {
        if (this.phoneMeta.number.startsWith(`+`)) {
          this.phone = this.phoneMeta.number
        } else {
          this.phone = `+${newVal.dialCode}${this.phoneMeta.number}`
        }
        this.tryFormat(this.phone, null)
      }
    },
    mounted() {
      this.$watch('value', this.handleValueChange, { immediate: true })
      this.$watch('prefix', this.handlePrefixChange, { immediate: true })
      if (this.detectCountry && !this.value && !this.prefix) {
        this.getClientCountry()
      }
    },
    methods: {
      removeWhitespaces(value) {
        if (!value) return value
        return value.replace(/ /g, '')
      },
      tryFormat(value, evt) {
        const fValue = this.format(value, evt)
        this.phone = fValue
        this.emitInput(fValue)
      },
      emitInput(value) {
        this.$emit('input', this.removeWhitespaces(value))
        this.$emit('input-data', this.inputData)
      },
      focusSelect() {
        this.handleFocus()
        this.$refs.select.focus()
      },
      format(value) {
        let formatter = new AsYouType(this.activeCountry.iso2)
        let formatted = formatter.input(value)
        this.phoneMeta = {
          number: formatter.getNationalNumber(),
          prefix: `+${formatter.countryCallingCode}`
        }
        return formatted
      },
      async formatAndSetCountry(value) {
        const formatter = new AsYouType()// eslint-disable-line
        formatter.input(value)
        // Find inputted country in the countries list
        this.activeCountry = this.allCountries.find(ele =>
          ele.iso2.toUpperCase() === formatter.country) || this.activeCountry
      },
      findCountry(countryIso = '') {
        return this.allCountries.find(country => country.iso2.toLowerCase() === countryIso.toLowerCase()) ||
          this.allCountries[0]
      },
      handleInput(value, evt) {
        this.tryFormat(value, evt)
      },
      handleBlur() {
        this.$emit('blur')
      },
      handleFocus() {
        this.$emit('focus')
      },
      setPrefix() {
        if (this.activeCountry) {
          let { dialCode } = this.activeCountry
          if (!this.phone || !this.phone.startsWith(`+${dialCode}`)) {
            this.phone = `+${this.activeCountry.dialCode}${this.phoneMeta.number}`
          }
        }
      },
      /* Public method */
      getClientCountry(inputCode) {
        let inputCountry = { code: inputCode }
        let defaultCountry = this.countries.find(c => c.primary) || { code: 'nl' }
        let code = inputCode ? inputCountry.code : defaultCountry.code

        this.activeCountry = this.findCountry(code)
        this.setPrefix()
      },
      async handleValueChange(newVal) {
        let rawVal = this.removeWhitespaces(newVal)
        let rawPhone = this.removeWhitespaces(this.phone)
        if (rawVal !== rawPhone) {
          this.phoneMeta.number = rawVal
          this.phone = rawVal
          await this.formatAndSetCountry(this.phone)
        }
      },
      handlePrefixChange() {
        this.handleValueChange(this.value)
      }
    }
  }
</script>
<style lang="scss">
  .input-phone-wrapper .el-select.country-select {
    width: 60px;
  }

  .input-phone-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    .el-form-item__content {
      width: 100%;
      margin-top: 5px;
    }

    .input-phone__content {
      display: flex;
    }

    .country-select .el-input__inner,
    .country-select .el-input.is-focus .el-input__inner {
      width: 25px;
    }

    .phone-input input {
      margin-left: 5px;
      width: calc(100% - 5px);
    }

    .phone-input {
      width: 100%;
    }

    .el-select .el-input .el-select__caret {
      display: flex;
      align-items: center;
      height: 100%;
      width: 14px;
    }

    .el-select .el-input.is-focus .el-input__inner {
      border-color: var(--color-primary);
    }
  }


  .country-option {
    display: flex;
    margin-top: 5px;
    margin-bottom: 5px;
    align-items: center;
  }

  .country-option__icon {
    width: 30px;
    height: auto;
    margin-right: 10px;
    margin-top: 10px;
  }

  .selected-country__icon {
    cursor: pointer;
  }

  .el-select-dropdown__item.country-option__item {
    display: flex;
    align-items: center;
  }
</style>
